"use client";

import { useMemo } from "react";
import type {
  Proposal,
  ProposalBatch,
} from "@stockline/shared/types/ai/proposal";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import type { PartnerResponse } from "@/app/api/types/partner";
import type { ProductResponse } from "@/app/api/types/product";
import type { UnitResponse } from "@/app/api/types/unit";
import {
  useGlobalPartners,
  useGlobalProducts,
} from "@/query/hooks/useUnifiedCache";
import { useUnits } from "@/query/hooks";
import { usePreOrder } from "@/query/hooks/usePreOrders/queries";
import type { UUID } from "@/app/api/types";
import { PreOrderSection } from "@/app/recap/components/PreOrderSection";
import { useStandaloneColumns } from "@/app/recap/hooks/useRecapColumns";
import {
  NUMERIC_FLOW_FIELDS,
  buildVirtualFlow,
  buildVirtualPreOrder,
  toNum,
} from "@/app/recap/utils/proposalBuilders";
import type {
  FlowProposalAnnotation,
  ProposalPreOrder,
} from "@/app/recap/types/proposal.types";
import { ProposalCardShell } from "./ProposalCardShell";
import type { ProposalCardInnerProps } from "./proposal-card-shared";

type AnnotatedFlow = PreOrderFlow & { __proposalDiff?: FlowProposalAnnotation };

/**
 * Stringify a value safely for diff rendering. Plain `String(obj)` would
 * produce `"[object Object]"`; we JSON-stringify objects so the user sees
 * the structural change (e.g. `{location_id: "x"}`).
 */
function stringifyMaybe(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === "string") return v;
  if (
    typeof v === "number" ||
    typeof v === "boolean" ||
    typeof v === "bigint"
  ) {
    return v.toString();
  }
  return JSON.stringify(v);
}

function findCreatePreOrderProposal(batch: ProposalBatch): Proposal | null {
  return (
    batch.proposals.find(
      (p) => p.entityType === "pre_order" && p.operation === "create",
    ) ?? null
  );
}

function findUpdatePreOrderProposal(batch: ProposalBatch): Proposal | null {
  return (
    batch.proposals.find(
      (p) => p.entityType === "pre_order" && p.operation === "update",
    ) ?? null
  );
}

function pickFlowProposals(batch: ProposalBatch): Proposal[] {
  return batch.proposals.filter(
    (p) => p.entityType === "pre_order_flow" || p.entityType === "flow",
  );
}

/**
 * Synthesize a virtual pre-order shell from flow-only proposals (no parent
 * pre_order proposal in the batch). Lets standalone `flow`/`pre_order_flow`
 * batches reuse the FlowsTable rendering without a real pre-order header.
 */
function buildShellFromFlows(
  batch: ProposalBatch,
  flowProposals: Proposal[],
  productMap: Map<string, ProductResponse>,
  availableUnits: UnitResponse[],
): ProposalPreOrder {
  const shellId = `proposal-shell-${batch.id}`;
  const flows = flowProposals.map((fp, i) =>
    buildVirtualFlow(fp, shellId, i, productMap, availableUnits),
  );
  return {
    id: shellId,
    user_id: batch.userId,
    type: 0,
    status: 0,
    source_type: 3,
    partner_id: undefined,
    partner: null,
    employee_id: null,
    delivery_date: null,
    order_date: null,
    comment: null,
    pre_order_number: null,
    customer_po_number: null,
    created_at: batch.createdAt,
    updated_at: batch.updatedAt,
    flows,
    pre_order_metadata: { seen: false, modified_by_ai: true },
    __proposal: {
      batchId: batch.id,
      operation: "create",
      batch,
      proposals: batch.proposals,
    },
  } as unknown as ProposalPreOrder;
}

const HEADER_KEYS = new Set([
  "partner_id",
  "employee_id",
  "order_date",
  "delivery_date",
  "comment",
  "pre_order_number",
  "customer_po_number",
]);

function buildUpdateAnnotatedPreOrder(
  livePreOrder: PreOrder,
  batch: ProposalBatch,
  updateProposal: Proposal,
  productMap: Map<string, ProductResponse>,
  availableUnits: UnitResponse[],
): ProposalPreOrder {
  const payload = updateProposal.payload ?? {};
  const linesToChange = (payload.lines_to_change ?? []) as Array<
    Record<string, unknown>
  >;
  const linesToDelete = new Set((payload.lines_to_delete ?? []) as string[]);
  const linesToAdd = (payload.lines_to_add ?? []) as Array<
    Record<string, unknown>
  >;

  const changeMap = new Map<string, Record<string, unknown>>();
  for (const line of linesToChange) {
    const id = line.id as string | undefined;
    if (id) changeMap.set(id, line);
  }

  const annotatedFlows: AnnotatedFlow[] = (livePreOrder.flows ?? [])
    .filter((f) => !linesToDelete.has(f.id))
    .map((f) => {
      const change = changeMap.get(f.id);
      if (!change) return f;
      const fieldDiffs: Record<
        string,
        { oldValue?: string; newValue?: string }
      > = {};
      const merged: Record<string, unknown> = { ...f };
      for (const [key, value] of Object.entries(change)) {
        if (key === "id") continue;
        // Pydantic Decimal fields land as JSON strings; recap math (acc +,
        // a * b, .toFixed) needs real numbers. Coerce the known numeric
        // flow fields at the merge boundary — see proposalBuilders.ts toNum.
        merged[key] = NUMERIC_FLOW_FIELDS.has(key) ? toNum(value) : value;
        const oldRaw = (f as unknown as Record<string, unknown>)[key];
        fieldDiffs[key] = {
          oldValue: stringifyMaybe(oldRaw),
          newValue: stringifyMaybe(value),
        };
      }
      return {
        ...(merged as unknown as PreOrderFlow),
        __proposalDiff: {
          operation: "update",
          fieldDiffs,
          sourceProposalId: updateProposal.id,
        },
      };
    });

  const addedFlows: AnnotatedFlow[] = linesToAdd.map((line, i) => {
    const synthetic: Proposal = {
      ...updateProposal,
      id: `${updateProposal.id}::add::${i}`,
      entityType: "pre_order_flow",
      operation: "create",
      entityId: null,
      payload: line,
    };
    return buildVirtualFlow(
      synthetic,
      livePreOrder.id,
      annotatedFlows.length + i,
      productMap,
      availableUnits,
    );
  });

  const merged: Record<string, unknown> = { ...livePreOrder };
  for (const key of Object.keys(payload)) {
    if (HEADER_KEYS.has(key)) merged[key] = payload[key];
  }

  return {
    ...(merged as unknown as PreOrder),
    flows: [...annotatedFlows, ...addedFlows],
    __proposal: {
      batchId: batch.id,
      operation: "update",
      batch,
      proposals: batch.proposals,
      sourceProposalId: updateProposal.id,
    },
  } as ProposalPreOrder;
}

export function ProposalPreOrderCard(props: ProposalCardInnerProps) {
  const { batch } = props;
  const columns = useStandaloneColumns();
  const partnersQuery = useGlobalPartners();
  const productsQuery = useGlobalProducts({ includeAll: true });
  const { data: units = [] } = useUnits();

  const partners = useMemo(
    () => (partnersQuery.data as PartnerResponse[] | undefined) ?? [],
    [partnersQuery.data],
  );
  const products = useMemo(
    () => (productsQuery.data as ProductResponse[] | undefined) ?? [],
    [productsQuery.data],
  );
  const partnerMap = useMemo(
    () => new Map(partners.map((p) => [p.id as string, p])),
    [partners],
  );
  const productMap = useMemo(
    () => new Map(products.map((p) => [p.id as string, p])),
    [products],
  );

  const createProposal = useMemo(
    () => findCreatePreOrderProposal(batch),
    [batch],
  );
  const updateProposal = useMemo(
    () => findUpdatePreOrderProposal(batch),
    [batch],
  );
  const flowProposals = useMemo(() => pickFlowProposals(batch), [batch]);

  // Live current state of the PO an "update" proposal targets. Uses the
  // TanStack `usePreOrder` so a proposal whose PO isn't in local Dexie
  // (cross-tenant references, deep history, etc.) still resolves via the
  // REST fallback inside the hook's queryFn instead of staying stuck on
  // "Loading pre-order…". Proposal cards are a rare cold-deep-link
  // surface — the cache-first lookup inside `usePreOrder` covers warm
  // hits in zero round-trips, and the REST fallback covers everything
  // else.
  const liveQuery = usePreOrder(
    (updateProposal?.entityId ?? null) as unknown as UUID | null,
  );
  const livePreOrder = liveQuery.data ?? null;

  const virtualPreOrder = useMemo<ProposalPreOrder | null>(() => {
    if (createProposal) {
      return buildVirtualPreOrder(
        batch,
        createProposal,
        flowProposals,
        partnerMap,
        productMap,
        units,
      );
    }
    if (updateProposal && livePreOrder) {
      return buildUpdateAnnotatedPreOrder(
        livePreOrder,
        batch,
        updateProposal,
        productMap,
        units,
      );
    }
    if (!createProposal && !updateProposal && flowProposals.length > 0) {
      return buildShellFromFlows(batch, flowProposals, productMap, units);
    }
    return null;
  }, [
    batch,
    createProposal,
    updateProposal,
    flowProposals,
    livePreOrder,
    partnerMap,
    productMap,
    units,
  ]);

  // PreOrderSection renders its own approve/reject in PreOrderHeader, so
  // suppress the shell's footer actions to avoid duplicate buttons.
  return (
    <ProposalCardShell {...props} canAct={false}>
      <div className="px-3 pb-3">
        {virtualPreOrder ? (
          <div className="rounded-md border border-border/40 bg-background/60 max-h-[60vh] overflow-y-auto">
            <PreOrderSection
              preOrder={virtualPreOrder}
              columns={columns}
              // Flow-only batches (no parent `pre_order` proposal) cannot
              // persist edits — useProposalFlowHandlers/useProposalHeaderHandler
              // both look up `entityType === "pre_order"`. Keep the table
              // read-only in that case.
              editableProposal={
                batch.status === "pending" &&
                (createProposal != null || updateProposal != null)
              }
            />
          </div>
        ) : (
          <div className="text-xs text-muted-foreground py-4 text-center">
            {updateProposal && !livePreOrder
              ? "Loading pre-order…"
              : "No pre-order data"}
          </div>
        )}
      </div>
    </ProposalCardShell>
  );
}
